import { NavLink } from 'react-router-dom';
import {
  Home,
  Users,
  MapPin,
  Clock,
  Calendar,
  Building2,
  UserCheck,
  Briefcase,
  Bell,
  FileText,
  Settings,
  FileBarChart,
  Pill,
  ScanBarcode,
  Boxes,
  FileBox,
  ClipboardCheck,
  ClipboardList,
  IdCard,
  LineChart,
  Shield,
  UserCog
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { usePermissions } from '../../hooks/usePermissions';
import { useBioAgents, useBioTerminals } from '../../hooks/useBio';

type NavItem = {
  name: string;
  href: string;
  icon: typeof Home;
  section?: string; // раздел для проверки прав; без него — виден всегда
  hidden?: boolean;
  requiresBio?: boolean; // виден, только если заведён агент или терминал
};

const navigation: NavItem[] = [
  { name: 'Панель управления', href: '/', icon: Home },
  { name: 'Сотрудники', href: '/employees', icon: Users, section: 'employees' },
  { name: 'Отделы', href: '/departments', icon: Building2, section: 'departments' },
  { name: 'Должности', href: '/positions', icon: Briefcase, section: 'positions' },
  { name: 'Локации', href: '/locations', icon: MapPin, section: 'locations' },
  { name: 'Расписания', href: '/schedules', icon: Calendar, section: 'schedules' },
  { name: 'Посещаемость', href: '/attendance', icon: UserCheck, section: 'attendance' },
  { name: 'Карточки терминалов', href: '/bio-cards', icon: IdCard, section: 'attendance', requiresBio: true },
  { name: 'Учет времени', href: '/time-records', icon: Clock, section: 'time_records' },
  { name: 'Отчеты', href: '/reports', icon: FileBarChart, section: 'reports' },
  { name: 'Медикаменты', href: '/medications', icon: Pill, section: 'medications' },
  { name: 'Остатки маркировки', href: '/markirovka-stock', icon: Boxes, section: 'markirovka_stock' },
  { name: 'Накладные', href: '/markirovka-documents', icon: FileBox, section: 'markirovka_documents' },
  { name: 'Сверка маркировки', href: '/markirovka-recon', icon: ClipboardCheck, section: 'markirovka_recon' },
  { name: 'Заявки на остатки', href: '/stock-requisitions', icon: ClipboardList, section: 'stock_requisitions' },
  { name: 'Сканы назначений', href: '/assignment-scans', icon: ScanBarcode, section: 'assignment_scans' },
  { name: 'Фарм-аналитика', href: '/pharma-analytics', icon: LineChart, section: 'pharma_analytics' },
  { name: 'Уведомления', href: '/notifications', icon: Bell, section: 'notifications' },
  { name: 'Документы', href: '/documents', icon: FileText, section: 'documents', hidden: true }, // TODO: включить когда будет готов backend
  { name: 'Пользователи', href: '/users', icon: UserCog, section: 'users' },
  { name: 'Роли', href: '/roles', icon: Shield, section: 'roles' },
  { name: 'Настройки', href: '/settings', icon: Settings, section: 'settings' },
];

export function Sidebar() {
  const { can } = usePermissions();

  // Биометрия — не у всех: пункт про карточки терминалов появляется только
  // когда в организации заведён агент или устройство.
  const canViewAttendance = can('attendance', 'view');
  const { data: bioAgents } = useBioAgents({ enabled: canViewAttendance });
  const { data: bioTerminals } = useBioTerminals({ enabled: canViewAttendance });
  const hasBioDevices =
    (bioAgents?.length ?? 0) > 0 || (bioTerminals?.length ?? 0) > 0;

  // Фильтрация пунктов меню по правам роли текущего пользователя
  const visibleNavigation = navigation.filter((item) => {
    if (item.hidden) {
      return false;
    }
    if (item.requiresBio && !hasBioDevices) {
      return false;
    }
    // Пункты без раздела (Панель управления) видны всегда
    if (!item.section) {
      return true;
    }
    return can(item.section, 'view');
  });

  return (
    <div className="w-[280px] bg-gradient-to-b from-neutral-700 to-neutral-800 flex-shrink-0 shadow-xl flex flex-col h-full min-h-0">
      <div className="px-6 py-6 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3 group cursor-pointer">
          {/* Компактная иконка календаря с hover эффектом */}
          <div className="transition-transform duration-300 group-hover:scale-110">
            <svg width="40" height="40" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="sidebar-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#007BFF" />
                  <stop offset="100%" stopColor="#0056D2" />
                </linearGradient>
              </defs>
              <rect x="3" y="7" width="26" height="22" rx="4" fill="url(#sidebar-gradient)" />
              <rect x="3" y="7" width="26" height="7" rx="4" fill="#0056D2" />
              <rect x="3" y="10" width="26" height="4" fill="#0056D2" />
              <rect x="8" y="5" width="2" height="4" rx="1" fill="#0056D2" />
              <rect x="22" y="5" width="2" height="4" rx="1" fill="#0056D2" />
              <circle cx="10" cy="17" r="1.2" fill="white" opacity="0.9" />
              <circle cx="14" cy="17" r="1.2" fill="white" opacity="0.9" />
              <circle cx="18" cy="17" r="1.2" fill="white" opacity="0.9" />
              <circle cx="22" cy="17" r="1.2" fill="white" opacity="0.9" />
              <circle cx="10" cy="21" r="1.2" fill="white" opacity="0.9" />
              <circle cx="14" cy="21" r="1.2" fill="white" opacity="0.9" />
              <circle cx="18" cy="21" r="1.2" fill="white" opacity="0.9" />
              <circle cx="22" cy="21" r="3" fill="white" />
              <path d="M 20 21 L 21.5 22.5 L 24 19.5" stroke="#007BFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </div>

          <h1 className="text-2xl font-light tracking-tight transition-all duration-300">
            <span className="font-extrabold text-white" style={{ letterSpacing: '-0.5px' }}>Week</span>
            <span className="font-light text-tco-blue-300">app.kz</span>
          </h1>
        </div>
      </div>
      <nav className="px-4 py-6 flex-1 overflow-y-auto min-h-0 sidebar-scroll">
        <ul className="space-y-1.5">
          {visibleNavigation.map((item) => {
            return (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group',
                      isActive
                        ? 'gradient-blue text-white shadow-lg'
                        : 'text-neutral-300 hover:text-white hover:bg-white/10'
                    )
                  }
                  end={item.href === '/'}
                >
                  <item.icon className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
                  {item.name}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}